/** Insight turunan — SEMUA dihitung deterministik dari payload yang sudah diambil.
 *  Tidak ada tebakan, tidak ada LLM. Bila datanya tidak cukup, insight tidak dimunculkan
 *  (lebih baik kosong daripada mengarang). Selaras AVIO-302r §6: deterministik sebelum AI. */
export type Insight = { id: string; icon: string; text: string; tone?: "good" | "bad"; href?: string };

const idn = (x: number) => Intl.NumberFormat("id-ID").format(Math.round(x));
const d1 = (x: number) => x.toFixed(1).replace(".", ",");
const wib = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;

export function spikeInsight(days: Array<{ day: string; positive: number; neutral: number; negative: number }>, hrefOf: (d: string) => string): Insight | null {
  if (days.length < 5) return null;
  const tot = days.map((d) => ({ day: d.day, v: d.positive + d.neutral + d.negative }));
  const peak = tot.reduce((a, b) => (b.v > a.v ? b : a));
  const rest = tot.filter((x) => x.day !== peak.day).map((x) => x.v).sort((a, b) => a - b);
  if (!rest.length) return null;
  const median = rest[Math.floor(rest.length / 2)] || 1;
  const ratio = peak.v / median;
  if (ratio < 1.8) return null;
  return { id: "spike", icon: "fa-solid fa-bolt", tone: "bad",
    text: `Lonjakan ${d1(ratio)}× pada ${wib(peak.day)} — ${idn(peak.v)} mention vs median ${idn(median)}`,
    href: hrefOf(peak.day) };
}

export function negShareInsight(now: { positive: number; neutral: number; negative: number }, prev: { positive: number; neutral: number; negative: number }): Insight | null {
  const t = now.positive + now.neutral + now.negative;
  const p = prev.positive + prev.neutral + prev.negative;
  if (t < 20 || p < 20) return null;
  const a = (now.negative / t) * 100, b = (prev.negative / p) * 100;
  const diff = a - b;
  if (Math.abs(diff) < 1) return null;
  return { id: "negshare", icon: diff > 0 ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-arrow-trend-down",
    tone: diff > 0 ? "bad" : "good",
    text: `Porsi negatif ${diff > 0 ? "naik" : "turun"} ${d1(Math.abs(diff))} poin (${d1(b)}% → ${d1(a)}%)` };
}

export function topMoverInsight(cats: Array<{ ref: string; negative: number; prevNegative: number }>, hrefOf: (ref: string) => string): Insight | null {
  if (!cats.length) return null;
  const moved = cats.map((c) => ({ ...c, d: c.negative - (c.prevNegative ?? 0) })).sort((a, b) => Math.abs(b.d) - Math.abs(a.d))[0];
  if (!moved || Math.abs(moved.d) < 5) return null;
  return { id: "mover", icon: "fa-solid fa-layer-group", tone: moved.d > 0 ? "bad" : "good",
    text: `${moved.ref}: negatif ${moved.d > 0 ? "+" : ""}${idn(moved.d)} vs periode sebelumnya`,
    href: hrefOf(moved.ref) };
}

export function channelInsight(chans: Array<{ channel: string; total: number }>, hrefOf: (c: string) => string): Insight | null {
  const tot = chans.reduce((a, b) => a + b.total, 0);
  if (!tot || !chans.length) return null;
  const top = [...chans].sort((a, b) => b.total - a.total)[0];
  const share = (top.total / tot) * 100;
  if (share < 25) return null;
  return { id: "chan", icon: "fa-solid fa-mobile-screen",
    text: `${top.channel} menguasai ${d1(share)}% percakapan (${idn(top.total)})`,
    href: hrefOf(top.channel) };
}
